/**
 * Pick one or more collections, and optionally a subset of their documents.
 *
 * The reusable input for the Base solution pages (Explore, Create, Review,
 * Quality): "which documents should this work from?". The list is the caller's
 * grant-scoped `GET /indexes`, so it offers exactly what the backend will let
 * them query — collections they own plus those shared with them. Retrieval
 * re-checks access server-side regardless.
 *
 * Value semantics: one `CollectionSelection` per chosen collection;
 * `documentIds === null` means the whole collection, an array means only those
 * documents. Only `indexed` documents can be picked — nothing else has passages
 * to retrieve.
 */

import { useEffect, useState } from "react";
import {
  Badge,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { getIndex, listIndexes, type IndexDetailOut, type IndexOut } from "@/shared/api/indexes";
import { toastApiError } from "@/shared/ui/toast";
import { collectionStatusColor, nestTranslations, translationsLabel } from "./collectionMeta";
import { SourceIcon } from "./SourceIcon";

export interface CollectionSelection {
  indexId: string;
  name: string;
  /** `null` = the whole collection. */
  documentIds: string[] | null;
}

interface Props {
  value: CollectionSelection[];
  onChange: (value: CollectionSelection[]) => void;
  label?: string;
  /** Allow more than one collection. Default `true`. */
  multiple?: boolean;
  /** Only collections with status `ready` can be chosen. Default `true`. */
  readyOnly?: boolean;
  /** Offer narrowing a collection to some of its documents. Default `true`. */
  allowDocumentSubset?: boolean;
}

export function CollectionDocumentPicker({
  value,
  onChange,
  label = "Collections",
  multiple = true,
  readyOnly = true,
  allowDocumentSubset = true,
}: Props) {
  const getToken = useAccessToken();
  const [collections, setCollections] = useState<IndexOut[] | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        setCollections((await listIndexes(token)).items);
      } catch (err) {
        toastApiError("Could not list collections", err);
        setCollections([]);
      }
    })();
  }, [getToken]);

  const data = (collections ?? []).map((c) => ({
    value: c.index_id,
    label: c.name,
    disabled: readyOnly && c.status !== "ready",
  }));

  const setIds = (ids: string[]) => {
    const byId = new Map((collections ?? []).map((c) => [c.index_id, c]));
    onChange(
      ids.map(
        (id) =>
          value.find((v) => v.indexId === id) ?? {
            indexId: id,
            name: byId.get(id)?.name ?? id,
            documentIds: null,
          },
      ),
    );
  };

  const update = (next: CollectionSelection) =>
    onChange(value.map((v) => (v.indexId === next.indexId ? next : v)));

  const placeholder = collections === null ? "Loading…" : "Choose a collection";
  const nothing = readyOnly ? "No ready collections available to you" : "No collections";

  return (
    <Stack gap="xs">
      {multiple ? (
        <MultiSelect
          label={label}
          placeholder={placeholder}
          data={data}
          value={value.map((v) => v.indexId)}
          onChange={setIds}
          disabled={collections === null}
          nothingFoundMessage={nothing}
          searchable
          clearable
        />
      ) : (
        <Select
          label={label}
          placeholder={placeholder}
          data={data}
          value={value[0]?.indexId ?? null}
          onChange={(id) => setIds(id ? [id] : [])}
          disabled={collections === null}
          nothingFoundMessage={nothing}
          searchable
          clearable
        />
      )}
      {collections !== null && collections.length === 0 && (
        <Text size="xs" c="dimmed">
          No collections are available to you. Ask a Developer to share one.
        </Text>
      )}
      {allowDocumentSubset &&
        value.map((sel) => (
          <DocumentSubset
            key={sel.indexId}
            selection={sel}
            status={collections?.find((c) => c.index_id === sel.indexId)?.status}
            onChange={update}
          />
        ))}
    </Stack>
  );
}

function DocumentSubset({
  selection,
  status,
  onChange,
}: {
  selection: CollectionSelection;
  status: string | undefined;
  onChange: (next: CollectionSelection) => void;
}) {
  const getToken = useAccessToken();
  const [detail, setDetail] = useState<IndexDetailOut | null>(null);
  const subset = selection.documentIds !== null;

  useEffect(() => {
    if (!subset || detail) return;
    void (async () => {
      try {
        const token = await getToken();
        setDetail(await getIndex(token, selection.indexId));
      } catch (err) {
        toastApiError("Could not load the collection's documents", err);
      }
    })();
  }, [subset, detail, getToken, selection.indexId]);

  const chosen = new Set(selection.documentIds ?? []);
  const toggle = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ...selection, documentIds: [...next] });
  };

  return (
    <Paper withBorder radius="sm" p="xs">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {selection.name}
          </Text>
          {status && (
            <Badge size="xs" variant="light" color={collectionStatusColor(status)}>
              {status}
            </Badge>
          )}
        </Group>
        <Switch
          size="xs"
          label="Only some documents"
          checked={subset}
          onChange={(e) =>
            onChange({ ...selection, documentIds: e.currentTarget.checked ? [] : null })
          }
        />
      </Group>
      {subset &&
        (detail === null ? (
          <Loader size="xs" mt="xs" />
        ) : (
          <ScrollArea.Autosize mah={220} mt="xs">
            <Stack gap={4}>
              {nestTranslations(detail.documents).map(({ doc: d, depth }) => (
                <Group key={d.document_id} gap="xs" wrap="nowrap" pl={depth ? "lg" : undefined}>
                  <Checkbox
                    size="xs"
                    aria-label={`Use ${d.display_name}`}
                    checked={chosen.has(d.document_id)}
                    disabled={d.status !== "indexed"}
                    onChange={() => toggle(d.document_id)}
                  />
                  <SourceIcon kind={d.source_kind} />
                  <Text size="xs" c={d.status === "indexed" ? undefined : "dimmed"} truncate>
                    {d.display_name}
                    {d.status !== "indexed" && ` (${d.status})`}
                  </Text>
                  {depth === 0 && translationsLabel(detail.documents, d.document_id) && (
                    <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                      {translationsLabel(detail.documents, d.document_id)}
                    </Text>
                  )}
                </Group>
              ))}
              {detail.documents.length === 0 && (
                <Text size="xs" c="dimmed">
                  This collection has no documents.
                </Text>
              )}
            </Stack>
          </ScrollArea.Autosize>
        ))}
    </Paper>
  );
}
